import type { Surface } from '@funkit/connect-core'
import { useEffect, useRef } from 'react'

import { logger } from '../../logging/logger'
import { useFunkitTranslation } from '../../providers/FunkitI18nProvider'
import { FiatStatusScreen } from '../FiatStatusScreen'
import type { SurfaceAdapter, SurfaceContext } from './adapter'
import {
  redeemStripeSeamlessSignIn,
  stripeSeamlessClientSecret,
} from './stripeSeamlessSignIn'
import type { StripeOnrampClient, StripeOnrampError } from './stripeOnramp'

/**
 * The calls the backend permits on this surface, as it names them. Kept as the
 * provider's own strings rather than re-encoded into a client vocabulary: the
 * surface is Stripe's, and a second set of names for the same three SDK methods
 * is a mapping to keep in sync for no gain.
 */
const STRIPE_KYC_CALLS = [
  'attachKycInfo',
  'presentKycInfoVerification',
  'verifyIdentity',
] as const
type StripeKycCall = (typeof STRIPE_KYC_CALLS)[number]

export interface StripeKycReconciliation {
  allowedCalls: readonly StripeKycCall[]
}

/**
 * Stripe's KYC reconciliation surface. `kyc.assess` emits it whenever the Link
 * user has no `kyc_verified` verification, and names the SDK calls the client
 * may make to obtain one.
 */
export function matchStripeKycParams(
  surface: Surface,
): StripeKycReconciliation | undefined {
  if (surface.kind !== 'AUTH_COMPONENT' || surface.provider !== 'STRIPE') {
    return undefined
  }
  const params: unknown = surface.params
  if (params === null || typeof params !== 'object') return undefined
  const record = params as Record<string, unknown>
  if (record.action !== 'RECONCILE_KYC') return undefined

  const allowed = Array.isArray(record.allowedCalls) ? record.allowedCalls : []
  const allowedCalls = STRIPE_KYC_CALLS.filter((call) => allowed.includes(call))
  // A reconciliation that permits nothing this build can run is not a surface
  // it can draw; declining sends it to the unsupported notice, which names the
  // provider, rather than to a screen that would sit there doing nothing.
  //
  // `attachKycInfo` alone counts as nothing: it posts what the form already
  // collected and draws no sheet, so the round has no step for the buyer to
  // finish and the screen waits on an interaction that never comes.
  const drawsSomething = allowedCalls.some((call) => call !== 'attachKycInfo')
  return drawsSomething ? { allowedCalls } : undefined
}

/**
 * `response` is required rather than optional: the pay sheet's adapter takes it
 * optionally and this one was written without it at all, so every call on this
 * surface ran against a Link session nothing had woken. A caller with nothing to
 * pass says so with `undefined`; one that forgets no longer compiles.
 */
export function stripeKycAdapter(
  stripe: StripeOnrampClient,
  response: object | undefined,
): SurfaceAdapter {
  return (surface) => {
    const reconciliation = matchStripeKycParams(surface)
    if (reconciliation === undefined) return undefined
    // Read on claim, exactly as the pay sheet reads it: the credential rides the
    // response that carried this surface, so it is as old as the surface and a
    // later render must not pick up a newer one.
    const seamlessClientSecret = stripeSeamlessClientSecret(response)
    return {
      control: 'HANDOFF',
      render: (context) => (
        <StripeKycReconciliationSurface
          stripe={stripe}
          reconciliation={reconciliation}
          seamlessClientSecret={seamlessClientSecret}
          context={context}
        />
      ),
    }
  }
}

/** The contract's `KycSdkResultReport`, under the `result` key the transition expects. */
const completed = () => ({ result: { outcome: 'COMPLETED' as const } })
const failed = (errorType: string) => ({
  result: { outcome: 'FAILED' as const, errorType },
})

type Outcome = ReturnType<typeof completed | typeof failed>

/**
 * `stage:code`, the shape `collectApplePay` already uses. Stripe answers most of these with a bare
 * `Failed`, which says nothing about which of the three calls refused; the stage is the only part
 * that identifies it. Everything Stripe gives beyond the code is logged rather than folded in here,
 * because this string travels to the backend as `errorType`.
 */
/**
 * The buyer closed Stripe's KYC sheet.
 *
 * Not a failure: nothing is wrong, nothing needs reporting to support, and the
 * only right answer is the quote screen they came from. Carried as a code for
 * the same reason `STRIPE_PAYMENT_CANCELLED` is — `onFailed` already routes
 * back to entry, and the screen suppresses its alert and its terminal for this
 * one code.
 */
export const STRIPE_KYC_CANCELLED = 'stripe_kyc:cancelled'

/**
 * Stripe handed control back for an address it wants corrected.
 *
 * Its own code, but the same support terminal as any other refusal, because
 * today there is nowhere better to send the buyer: `POST /fiat/kyc/capture`
 * ignores the report, and `assess` answers any unverified customer with this
 * same surface, so every client-side route back — remounting, re-quoting —
 * returns here and asks Stripe the identical question. The code exists to make
 * that population countable before the round trip that can actually fix it.
 */
export const STRIPE_KYC_ADDRESS_UPDATE = 'stripe_kyc:update_address'

/** Stripe's own spelling, with the one `l`. Every call on this surface uses it. */
const STRIPE_CANCELLED_ERROR_CODE = 'Canceled'

/**
 * A dismissal, or a real refusal carrying the stage that produced it. Both leave
 * this surface, but only one of them is worth telling the buyer about.
 */
function stageOutcome(stage: string, error: StripeOnrampError): Outcome {
  if (error.code === STRIPE_CANCELLED_ERROR_CODE) {
    logger.info('connect-rn:fiat:stripe_kyc_cancelled', { stage })
    return failed(STRIPE_KYC_CANCELLED)
  }
  return failed(stageCode(stage, error))
}

function stageCode(stage: string, error: StripeOnrampError): string {
  logger.warn('connect-rn:fiat:stripe_kyc_stage_failed', {
    stage,
    code: error.code,
    message: error.message,
    onrampErrorType: error.onrampErrorType,
    stripeErrorCode: error.stripeErrorCode,
    apiErrorCode: error.apiErrorCode,
    apiErrorType: error.apiErrorType,
    developerMessage: error.developerMessage,
    stripeRequestId: error.requestId,
  })
  return `${stage}:${error.code}`
}

function StripeKycReconciliationSurface({
  stripe,
  reconciliation,
  seamlessClientSecret,
  context,
}: {
  stripe: StripeOnrampClient
  reconciliation: StripeKycReconciliation
  seamlessClientSecret: string | undefined
  context: SurfaceContext
}) {
  const { t } = useFunkitTranslation()
  const mounted = useRef(true)
  const started = useRef(false)
  const contextRef = useRef(context)
  contextRef.current = context
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  const allows = (call: StripeKycCall) =>
    reconciliation.allowedCalls.includes(call)

  /**
   * The information half of Stripe's order: attach what we hold, then let its
   * sheet confirm it. Each call runs only while the backend permits it, so a
   * narrowed `allowedCalls` shortens the sequence instead of failing it.
   */
  const reconcileInfo = useEffectEventLike(async () => {
    // Before any of the three calls, not after one fails. Every call on this
    // surface runs against the SDK's Link consumer session, which dies with the
    // app process while our stored grant outlives it; Stripe then answers "No
    // active Link consumer is available" with no way to test for it first. A
    // refusal is not fatal: the interactive round is the documented fallback.
    if (seamlessClientSecret !== undefined) {
      logger.info('connect-rn:fiat:stripe_kyc_stage_started', {
        stage: 'seamless_signin',
      })
      const authenticated = await redeemStripeSeamlessSignIn(
        stripe,
        seamlessClientSecret,
      )
      logger.info('connect-rn:fiat:stripe_kyc_stage_completed', {
        stage: 'seamless_signin',
        authenticated,
      })
    }

    if (allows('presentKycInfoVerification')) {
      // Stripe requires info to have been attached before it will present this
      // sheet. The form round does that: `FiatDepositAmountStep` pushes the
      // answers through `attachKycInfo` before it submits. The stage log is
      // what tells us whether that precondition, rather than the sheet itself,
      // is what refused.
      logger.info('connect-rn:fiat:stripe_kyc_stage_started', {
        stage: 'present_kyc_info_verification',
      })
      const verified = await stripe.presentKycInfoVerification(null)
      if (verified.error !== undefined) {
        return stageOutcome('present_kyc_info_verification', verified.error)
      }
      logger.info('connect-rn:fiat:stripe_kyc_stage_completed', {
        stage: 'present_kyc_info_verification',
        status: verified.status,
      })
      // Stripe hands control back for an address it will not collect itself.
      // Not answered here: every other rail collects fields through the
      // backend's `INPUT_REQUIRED` form, and a form built inside a surface
      // adapter would be the only one of its kind. Until the backend re-asks on
      // this report, the buyer's only way on is support.
      if (verified.status === 'UpdateAddress') {
        return failed(STRIPE_KYC_ADDRESS_UPDATE)
      }
    }

    if (allows('verifyIdentity')) {
      logger.info('connect-rn:fiat:stripe_kyc_stage_started', {
        stage: 'verify_identity',
      })
      const identity = await stripe.verifyIdentity()
      if (identity.error !== undefined) {
        return stageOutcome('verify_identity', identity.error)
      }
      logger.info('connect-rn:fiat:stripe_kyc_stage_completed', {
        stage: 'verify_identity',
      })
    }
    return completed()
  })

  const run = (step: () => Promise<Outcome>) => {
    void step()
      .then((outcome) => {
        if (!mounted.current) {
          return
        }
        const report = outcome.result
        if (report.outcome === 'COMPLETED') {
          logger.info('connect-rn:fiat:stripe_kyc_reconciled')
          contextRef.current.report(outcome)
          return
        }
        // `onFailed`, not a FAILED report: standing is re-read server-side, so
        // reporting a refusal returns the same `KYC/CAPTURE` state and this
        // surface remounts with nothing left to run — a spinner that never
        // resolves. `onFailed` is the seam the screen already owns for "this
        // attempt is over", and every other rail reaches it the same way.
        // A dismissal already logged itself as one; repeating it as a failure is
        // how a deliberate choice starts looking like a broken integration.
        // Logged under its own name rather than folded into the generic
        // failure: this is the one refusal a backend change could turn into a
        // form round, and its rate is what says whether that is worth building.
        if (report.errorType === STRIPE_KYC_ADDRESS_UPDATE) {
          logger.warn('connect-rn:fiat:stripe_kyc_address_update_requested')
        } else if (report.errorType !== STRIPE_KYC_CANCELLED) {
          logger.warn('connect-rn:fiat:stripe_kyc_failed', {
            code: report.errorType,
          })
        }
        contextRef.current.onFailed(report.errorType)
      })
      .catch((error: unknown) => {
        if (!mounted.current) {
          return
        }
        const code =
          error instanceof Error ? error.message : 'stripe_kyc_failed'
        logger.warn('connect-rn:fiat:stripe_kyc_failed', { code })
        contextRef.current.onFailed(code)
      })
  }

  useEffect(() => {
    if (started.current) {
      return
    }
    started.current = true
    run(() => reconcileInfo())
    // Runs once per surface; `run` closes over refs that stay current.
  }, [])

  // Stripe's own sheet owns the screen while this runs; the introduction before
  // it belongs to `FiatDocumentCaptureStep`, which the KYC_CAPTURE branch already
  // wraps every provider in. This is only the gap between the two.
  return (
    <FiatStatusScreen
      tone="waiting"
      title={t('fiat.stripeAuthorizing')}
      onClose={context.onClose}
    />
  )
}

/**
 * Keeps the async sequence out of React's dependency graph without pulling in a
 * new dependency: the callback is re-read from a ref on every call, so it never
 * needs to be a hook dependency and never captures a stale `stripe`.
 */
function useEffectEventLike<A extends unknown[], R>(
  callback: (...args: A) => Promise<R>,
) {
  const ref = useRef(callback)
  ref.current = callback
  return useRef((...args: A) => ref.current(...args)).current
}
